import React from 'react';
import { Box, Flex, ScrollArea, Text, TextField } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { cx } from 'class-variance-authority';
import { ExpandMoreIcon } from 'shared/icons';

import {
   AutocompleteV2ViewModel,
   AutocompleteV2ViewModelState,
} from './autocomplete-v2.view-model';
import './styles/autocomplete-v2.scss';

export type AutocompleteV2ResetInputOnClose = 'never' | 'if-unselected' | 'always';
export type AutocompleteV2OptionKey = string | number;

export interface AutocompleteV2RootProps<TValue> {
   items: TValue[];
   viewModel?: AutocompleteV2ViewModel<TValue>;
   value?: TValue | null;
   defaultValue?: TValue | null;
   selectedKey?: AutocompleteV2OptionKey | null;
   defaultSelectedKey?: AutocompleteV2OptionKey | null;
   inputValue?: string;
   defaultInputValue?: string;
   open?: boolean;
   defaultOpen?: boolean;
   placeholder?: string;
   disabled?: boolean;
   loading?: boolean;
   loadingText?: React.ReactNode;
   emptyText?: React.ReactNode;
   closeOnSelect?: boolean;
   openOnFocus?: boolean;
   clearValueOnInputChange?: boolean;
   resetInputOnClose?: AutocompleteV2ResetInputOnClose;
   getOptionValue?: (item: TValue) => AutocompleteV2OptionKey;
   getOptionKey?: (item: TValue, index: number) => React.Key;
   getOptionLabel?: (item: TValue) => string;
   renderOption?: (item: TValue) => React.ReactNode;
   onValueChange?: (value: TValue | null) => void;
   onSelectedKeyChange?: (key: AutocompleteV2OptionKey | null) => void;
   onInputValueChange?: (value: string) => void;
   onSearch?: (value: string) => void;
   onOpenChange?: (open: boolean) => void;
   onSelect?: (value: TValue) => void;
}

const AutocompleteV2RootView = <TValue,>({
   items,
   viewModel,
   value,
   defaultValue = null,
   selectedKey,
   defaultSelectedKey = null,
   inputValue,
   defaultInputValue,
   open,
   defaultOpen = false,
   placeholder,
   disabled = false,
   loading = false,
   loadingText = 'Завантаження...',
   emptyText = 'Опцій не знайдено',
   closeOnSelect = true,
   openOnFocus = true,
   clearValueOnInputChange = true,
   resetInputOnClose = 'if-unselected',
   getOptionValue,
   getOptionKey,
   getOptionLabel,
   renderOption,
   onValueChange,
   onSelectedKeyChange,
   onInputValueChange,
   onSearch,
   onOpenChange,
   onSelect,
}: AutocompleteV2RootProps<TValue>) => {
   const [internalViewModel] = React.useState(
      () =>
         new AutocompleteV2ViewModel<TValue>({
            value:
               defaultValue ??
               findItemBySelectedKey(
                  items,
                  defaultSelectedKey,
                  getOptionValue ?? defaultGetOptionValue
               ),
            inputValue: defaultInputValue,
            open: defaultOpen,
         } satisfies AutocompleteV2ViewModelState<TValue>)
   );
   const rootRef = React.useRef<HTMLDivElement | null>(null);
   const inputRef = React.useRef<HTMLInputElement | null>(null);
   const vm = viewModel ?? internalViewModel;
   const optionLabel = getOptionLabel ?? defaultGetOptionLabel;
   const optionValue = getOptionValue ?? defaultGetOptionValue;
   const currentValue =
      value ??
      (selectedKey !== undefined
         ? findItemBySelectedKey(items, selectedKey, optionValue)
         : vm.value);
   const currentOpen = open ?? vm.opened;
   const currentInputValue =
      inputValue ??
      getDisplayInputValue({
         open: currentOpen,
         selectedValue: currentValue,
         typedValue: vm.inputValue,
         getOptionLabel: optionLabel,
      });

   const handleOpenChange = (nextOpen: boolean) => {
      if (nextOpen) {
         inputRef.current?.focus();
      }

      if (open === undefined) {
         vm.setOpened(nextOpen);
      }

      onOpenChange?.(nextOpen);

      if (!nextOpen) {
         if (resetInputOnClose === 'always') {
            resetUncontrolledInput(currentValue, inputValue, vm, optionLabel);
         }

         if (resetInputOnClose === 'if-unselected' && !currentValue) {
            resetUncontrolledInput(currentValue, inputValue, vm, optionLabel);
         }
      }
   };

   const handleClose = () => {
      handleOpenChange(false);
   };

   const handleInputChange = (nextInputValue: string) => {
      if (!currentOpen) {
         if (open === undefined) {
            vm.setOpened(true);
         }
         onOpenChange?.(true);
      }

      if (
         clearValueOnInputChange &&
         currentValue &&
         nextInputValue !== optionLabel(currentValue)
      ) {
         if (value === undefined && selectedKey === undefined) {
            vm.setValue(null);
         }
         onValueChange?.(null);
         onSelectedKeyChange?.(null);
      }

      if (inputValue === undefined) {
         vm.setInputValue(nextInputValue);
      }

      onInputValueChange?.(nextInputValue);
      onSearch?.(nextInputValue);
   };

   const handleSelect = (item: TValue) => {
      const nextLabel = optionLabel(item);

      if (value === undefined && selectedKey === undefined) {
         vm.setValue(item);
      }

      if (inputValue === undefined) {
         vm.setInputValue(nextLabel);
      }

      onValueChange?.(item);
      onInputValueChange?.(nextLabel);
      onSelectedKeyChange?.(optionValue(item));
      onSelect?.(item);

      if (closeOnSelect) {
         handleClose();
      }
   };

   return (
      <div
         ref={rootRef}
         className="autocomplete"
         onBlur={() => {
            requestAnimationFrame(() => {
               if (!rootRef.current?.contains(document.activeElement)) {
                  handleClose();
               }
            });
         }}
      >
         <Flex className="autocomplete__trigger" justify="between" width="100%">
            <TextField.Root
               size="3"
               color="blue"
               ref={inputRef}
               disabled={disabled}
               className="autocomplete__search"
               placeholder={placeholder}
               value={currentInputValue}
               onChange={(event) => {
                  handleInputChange(event.target.value);
               }}
               onFocus={() => {
                  if (!disabled && openOnFocus && !currentOpen) {
                     handleOpenChange(true);
                  }
               }}
               onKeyDown={(event) => {
                  if (!disabled && !currentOpen) {
                     handleOpenChange(true);
                  }

                  if (event.key === 'Escape') {
                     handleClose();
                  }
               }}
            >
               <TextField.Slot side="right">
                  <Text
                     className={cx(
                        'autocomplete__indicator',
                        currentOpen && 'autocomplete__indicator--opened'
                     )}
                  >
                     <ExpandMoreIcon width={24} height={24} />
                  </Text>
               </TextField.Slot>
            </TextField.Root>
         </Flex>

         {currentOpen ? (
            <Box className="autocomplete__content">
            {loading ? (
               <Box className="autocomplete__message">
                  <Text color="gray">{loadingText}</Text>
               </Box>
            ) : items.length > 0 ? (
               <ScrollArea type="auto" scrollbars="both" style={{ maxHeight: '170px' }}>
                  {items.map((item, index) => {
                     return (
                        <AutocompleteV2Row
                           key={(getOptionKey ?? defaultGetOptionKey)(item, index)}
                           item={item}
                           getOptionLabel={optionLabel}
                           renderOption={renderOption}
                           onSelect={handleSelect}
                        />
                     );
                  })}
               </ScrollArea>
            ) : (
               <Box className="autocomplete__message">
                  <Text color="gray">{emptyText}</Text>
               </Box>
            )}
            </Box>
         ) : null}
      </div>
   );
};

function AutocompleteV2Row<TValue>({
   item,
   getOptionLabel,
   renderOption,
   onSelect,
}: {
   item: TValue;
   getOptionLabel: (item: TValue) => string;
   renderOption?: (item: TValue) => React.ReactNode;
   onSelect: (item: TValue) => void;
}) {
   return (
      <div className="multi-select__row-item">
         <div
            className="autocomplete__option"
            onMouseDown={(event) => {
               event.preventDefault();
            }}
            onClick={() => {
               onSelect(item);
            }}
         >
            {renderOption ? renderOption(item) : getOptionLabel(item)}
         </div>
      </div>
   );
}

function defaultGetOptionKey<TValue>(item: TValue, index: number): React.Key {
   const value = defaultGetOptionValue(item);
   return value ?? `${defaultGetOptionLabel(item)}-${index}`;
}

function defaultGetOptionValue<TValue>(item: TValue): AutocompleteV2OptionKey {
   if (typeof item === 'string' || typeof item === 'number') {
      return item;
   }

   if (typeof item === 'object' && item) {
      const option = item as Record<string, unknown>;
      const id = option.id;

      if (typeof id === 'string' || typeof id === 'number') {
         return id;
      }
   }

   return defaultGetOptionLabel(item);
}

function defaultGetOptionLabel<TValue>(item: TValue): string {
   if (typeof item === 'string') {
      return item;
   }

   if (typeof item === 'number') {
      return item.toString();
   }

   if (typeof item === 'object' && item) {
      const option = item as Record<string, unknown>;

      if (typeof option.label === 'string') {
         return option.label;
      }

      if (typeof option.name === 'string') {
         return option.name;
      }
   }

   return String(item ?? '');
}

function findItemBySelectedKey<TValue>(
   items: TValue[],
   selectedKey: AutocompleteV2OptionKey | null | undefined,
   getOptionValue: (item: TValue) => AutocompleteV2OptionKey
): TValue | null {
   if (selectedKey === null || selectedKey === undefined) {
      return null;
   }

   return items.find((item) => getOptionValue(item) === selectedKey) ?? null;
}

function getDisplayInputValue<TValue>({
   open,
   selectedValue,
   typedValue,
   getOptionLabel,
}: {
   open: boolean;
   selectedValue: TValue | null;
   typedValue: string;
   getOptionLabel: (item: TValue) => string;
}): string {
   if (open) {
      return typedValue;
   }

   if (selectedValue) {
      return getOptionLabel(selectedValue);
   }

   return typedValue;
}

function resetUncontrolledInput<TValue>(
   currentValue: TValue | null,
   inputValue: string | undefined,
   viewModel: AutocompleteV2ViewModel<TValue>,
   getOptionLabel: (item: TValue) => string
): void {
   if (inputValue !== undefined) {
      return;
   }

   viewModel.setInputValue(currentValue ? getOptionLabel(currentValue) : '');
}

export const AutocompleteV2Root = observer(AutocompleteV2RootView) as <TValue>(
   props: AutocompleteV2RootProps<TValue>
) => React.ReactElement;

export { AutocompleteV2Root as Root };
